import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';

/**
 * ActionMenu Component
 *
 * Dropdown menu with 3-dot icon (kebab menu) for table row actions.
 * Uses Preline's hs-dropdown for dropdown functionality.
 *
 * @param {Object} props
 * @param {Array} props.items - Array of menu items
 * @param {string} props.items[].label - Menu item label
 * @param {string} props.items[].icon - Iconify icon name
 * @param {Function} props.items[].onClick - Click handler
 * @param {string} [props.items[].variant] - 'default' | 'danger' (default: 'default')
 * @param {boolean} [props.items[].disabled] - Whether item is disabled
 *
 * @example
 * <ActionMenu
 *   items={[
 *     { label: 'Edit', icon: 'lucide:pencil', onClick: handleEdit },
 *     { label: 'Delete', icon: 'lucide:trash-2', onClick: handleDelete, variant: 'danger' }
 *   ]}
 * />
 */
export function ActionMenu({ items }) {
  const dropdownRef = useRef(null);

  useEffect(() => {
    // Initialize Preline dropdown when component mounts
    const initDropdown = async () => {
      if (dropdownRef.current && window.HSStaticMethods) {
        await new Promise(resolve => setTimeout(resolve, 10));
        window.HSStaticMethods.autoInit();
      }
    };

    initDropdown();
  }, []);

  return (
    <div ref={dropdownRef} className="hs-dropdown [--trigger:hover] relative inline-flex">
      <button
        type="button"
        className="hs-dropdown-toggle inline-flex items-center justify-center size-9 text-sm font-medium rounded-lg text-subtle hover:bg-brand/5 focus:outline-none cursor-pointer"
        aria-label="Actions menu"
      >
        <Icon icon="lucide:more-vertical" className="size-4" />
      </button>

      <div
        className="hs-dropdown-menu transition-[opacity,margin] duration-300 hs-dropdown-open:opacity-100 opacity-0 hidden min-w-40 bg-white shadow-md rounded-lg p-1 mt-2 after:h-4 after:absolute after:-bottom-4 after:start-0 after:w-full before:h-4 before:absolute before:-top-4 before:start-0 before:w-full z-10"
        role="menu"
      >
        {items.map((item, index) => {
          const textColor = item.variant === 'danger' ? 'text-red-600' : 'text-default';
          const hoverBg = item.variant === 'danger' ? 'hover:bg-red-50' : 'hover:bg-brand/5';

          return (
            <button
              key={index}
              type="button"
              className={`w-full flex items-center gap-x-3 py-2 px-3 rounded-lg text-sm ${textColor} ${hoverBg} focus:outline-none disabled:opacity-50 disabled:pointer-events-none cursor-pointer`}
              onClick={(e) => {
                e.stopPropagation();
                item.onClick();
              }}
              disabled={item.disabled}
              aria-label={item.label}
            >
              <Icon icon={item.icon} className="size-4 shrink-0" />
              {item.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

ActionMenu.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      variant: PropTypes.oneOf(['default', 'danger']),
      disabled: PropTypes.bool,
    })
  ).isRequired,
};
